import React, { useState, useEffect } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { onSnapshot } from "../utils/firestoreListen";
import { db, auth } from "../firebase";
import {
  FaShareAlt,
  FaExpandArrowsAlt,
  FaTimes
} from "react-icons/fa";
import ArtworkReactions from "./ArtworkReactions";

export default function PostModal({ post, onClose, onKudosChange }) {
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const postRef = doc(db, "artworks", post.id);

  useEffect(() => {
    const unsubscribe = onSnapshot(postRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setComments(data.comments || []);
        setLiked((data.likes || []).includes(auth.currentUser?.uid));
      }
    }, undefined, { purpose: 'PostModal-post-comments', component: 'PostModal', essential: true });
    return () => unsubscribe();
  }, [post.id]);

  const handleShare = () => {
    const shareData = {
      title: post.title,
      text: post.description,
      url: window.location.href,
    };
    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // Expanded fullscreen modal
  if (expanded) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center">
        <button
          onClick={() => setExpanded(false)}
          className="absolute top-6 right-6 bg-white/90 rounded-full p-3 shadow hover:bg-white z-60"
          title="Close"
        >
          <FaTimes size={28} />
        </button>
        <div className="flex flex-col items-center justify-center w-full h-full">
          <img
            src={post.imageUrl}
            alt={post.title}
            className="w-full h-full max-w-4xl max-h-[90vh] object-cover rounded-2xl border-4 border-amber-400 shadow-2xl"
            style={{ background: 'white' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-auto flex flex-col md:flex-row overflow-hidden relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-6 text-gray-700 text-3xl font-bold hover:text-red-500"
          aria-label="Close"
        >
          &times;
        </button>
        {/* Image Section */}
        <div className="md:w-2/3 w-full h-[80vh] flex justify-center items-center bg-white relative">
          {/* Expand Button (over image, top right) */}
          <button
            onClick={() => setExpanded(true)}
            className="absolute top-4 right-4 bg-white/80 rounded-full p-2 shadow hover:bg-white z-60"
            title="Expand"
          >
            <FaExpandArrowsAlt size={22} />
          </button>
          <img
            src={post.imageUrl}
            alt={post.title}
            className="w-full h-full object-cover rounded-2xl border-4 border-amber-400 shadow-xl"
            style={{ maxHeight: '100%', maxWidth: '100%' }}
          />
        </div>
        {/* Info Section */}
        <div className="md:w-1/3 w-full p-6 flex flex-col gap-4 bg-white">
          <h2 className="text-2xl font-bold text-amber-800">{post.title}</h2>
          <p className="text-sm text-gray-600">{post.description}</p>
          {/* Share Button */}
          <div className="flex space-x-4 items-center">
            <button
              onClick={handleShare}
              className="text-blue-500 text-2xl hover:scale-110 transition"
              title="Share"
            >
              <FaShareAlt />
            </button>
          </div>
          {/* Artwork Reactions - Unified Comment System */}
          <div className="mt-4 pt-4 border-t border-amber-200">
            <ArtworkReactions 
              artworkId={post.id} 
              artistId={post.uid}
              onKudosChange={onKudosChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
